<template>
  <div
    id="performance"
    class="px-4"
  >
    <v-row
      key="performancePortalAdvancedFilter"
      align="center"
      dense
      class="mt-0 mb-3"
    >
      <v-spacer />
      <v-col cols="auto">
        <v-btn
          color="primary"
          text
          class="text-none"
          @click="filtersDialog = true"
        >
          {{ $t('button.filters') }}
        </v-btn>
      </v-col>
      <v-col cols="auto">
        <layout-selector
          :selected-layout.sync="selectedLayout"
          :layouts.sync="layouts"
          @add-layout="openAddLayoutDialog"
          @edit-layout="openEditLayoutDialog"
        />
      </v-col>
    </v-row>

    <v-container
      v-if="variables.loading"
      class="d-flex justify-center align-center my-16"
    >
      <v-progress-circular
        indeterminate
        size="75"
      />
    </v-container>
    <div v-else>
      <grid-layout
        v-if="selectedLayout"
        :key="selectedLayout.name"
        :layout="adjustedGridLayout"
        :col-num="12"
        :row-height="70"
        :is-draggable="false"
        :is-resizable="false"
        :vertical-compact="true"
        :margin="[10, 10]"
        :use-css-transforms="true"
        @layout-ready="setLayoutReady()"
      >
        <grid-item
          v-for="item in adjustedGridLayout"
          :key="item.i"
          :x="item.x"
          :y="item.y"
          :w="item.w"
          :h="item.h"
          :i="item.i"
        >
          <div
            v-if="layoutReady"
            class="fill-height"
          >
            <div
              v-if="item.type === 'table'"
              class="fill-height"
            >
              <v-card class="fill-height rounded-lg">
                <v-card-title class="px-6 py-4">
                  <v-row
                    no-gutters
                    align="center"
                  >
                    <v-col>
                      <div class="subtitle-2 grey--text text--grey-100">
                        {{ $t('event_details.players') }}
                      </div>
                    </v-col>
                    <v-col
                      cols="auto"
                      class="pl-2"
                    >
                      <variable-selector
                        v-if="selectedLayout && variables.data"
                        :variables="variables.data"
                        :layout-variables.sync="tableHeaders"
                        :excluded-variables="['reason']"
                        @update="saveLayoutVariables"
                      />
                    </v-col>
                  </v-row>
                </v-card-title>
                <v-card-text class="pa-0">
                  <performance-general-table
                    :player-performance="filteredPlayers"
                    :loading="advancedPerformanceStats.loading || advancedPerformanceComparisonStats.loading"
                    :max-values="maxValues"
                    :table-headers="layoutVariables"
                    :reference-stats="advancedPerformanceComparisonStats.data.players.length > 0"
                    @select:player="selectPlayer($event)"
                  />
                </v-card-text>
              </v-card>
            </div>
            <div
              v-else-if="item.type === 'card'"
              class="fill-height"
            >
              <stat-card
                :loading="advancedPerformanceStats.loading || advancedPerformanceComparisonStats.loading"
                :title="getVariableName(item.key)"
                :value="teamStats[item.key]"
                :max="maxValues[item.key]"
                :color="getColorFromValue(teamStats[item.key], maxValues[item.key])"
              />
            </div>
            <div
              v-else-if="item.type === 'chart'"
              class="fill-height"
            >
              <performance-graph-individual
                v-if="individualChartKeys.includes(item.key)"
                :variables="variables.data"
                :chart="item"
                :stats="teamStats"
              />
              <performance-graph
                v-else
                class="fill-height"
                :players="filteredPlayers"
                :chart="item"
                :stats="teamStats"
              />
            </div>
          </div>
        </grid-item>
      </grid-layout>
    </div>

    <advanced-performance-filter-dialog
      :visible.sync="filtersDialog"
      :filters="advancedFilters"
      :comparison-filters="advancedCompareFilters"
      :events="events"
      :comparison-events="comparisonEvents"
      :players="allPlayers"
      :exercises="exercises"
      :comparion-exercises="comparisonExercises"
      @handle:advanced="fetchAdvancedPerformanceStats"
      @handle:comparison="fetchAdvancedPerformanceComparisonStats"
    />
  </div>
</template>

<script>
import {
  EventApiService,
  PlayerApiService,
  PlayerPositionApiService,
  PlayerStatusApiService
} from '@/data/services'
import PerformanceGeneralTable from '@/components/performance/PerformanceGeneralTable'
import { adjustGridForTableHeights, getColorForValue, processMaxValues, toCamelCase } from '@/utils/helpers'
import dayjs from 'dayjs'
import debounce from 'lodash/debounce'
import { orderBy } from 'lodash'
import { DashboardType } from '@/data/models/Dashboard'
import { EventType, EventTypes } from '@/data/models'
import StatCard from '@/components/StatCard.vue'
import VariableSelector from '@/components/performance/VariableSelector.vue'
import AdvancedPerformanceFilterDialog from '@/components/dialogs/AdvancedPerformanceFilterDialog.vue'
import { VariableApiService } from '@/data/services/VariableApiService'
import { LayoutApiService } from '@/data/services/LayoutApiService'
import PerformanceGraphIndividual from '@/components/performance/PerformanceGraphIndividual.vue'
import PerformanceGraph from '@/components/performance/PerformanceGraph.vue'
import { ChartApiService } from '@/data/services/ChartApiService'
import { GridItem, GridLayout } from 'vue-grid-layout'
import { EventBus } from '@/plugins/bus'
import { RouteNames } from '@/router/routes'
import LayoutSelector from '@/components/LayoutSelector.vue'
import { dashboardConfig } from '@/data/models/DashboardConfig'

export default {
  name: 'PerformanceAdvancedView',
  // eslint-disable-next-line vue/no-unused-components
  components: {
    LayoutSelector,
    PerformanceGraph,
    PerformanceGraphIndividual,
    AdvancedPerformanceFilterDialog,
    VariableSelector,
    StatCard,
    PerformanceGeneralTable,
    GridLayout,
    GridItem
  },
  data: () => ({
    currentTab: 'event',
    currentView: 'GPS',
    layoutReady: false,
    heartRateView: false,
    eventSelection: false,
    eventSelectionFilter: undefined,
    date: dayjs().format('YYYY-MM-DD'),
    events: {
      loading: false,
      data: []
    },
    comparisonEvents: {
      loading: false,
      data: []
    },
    playerStatuses: {
      data: [],
      loading: false,
      loaded: false,
      selectedId: undefined
    },
    playerPositions: {
      data: [],
      loading: false,
      loaded: false,
      selectedId: undefined
    },
    variables: {
      data: null,
      loading: false
    },
    filterDateAdvancedActive: false,
    advancedFilters: {
      dateRange: [],
      eventIds: [],
      teamId: 'all',
      teamIds: [],
      playerIds: [],
      exerciseIds: [],
      eventType: null,
      onlyOfficialMatch: false
    },
    advancedCompareFilters: {
      dateRange: [],
      eventIds: [],
      teamId: 'all',
      exerciseIds: [],
      eventType: null,
      onlyOfficialMatch: false
    },
    filters: {
      eventType: undefined,
      eventName: ''
    },
    allPlayers: {
      loading: false,
      data: []
    },
    exercises: {
      loading: false,
      data: []
    },
    comparisonExercises: {
      loading: false,
      data: []
    },
    advancedPerformanceStats: {
      loading: false,
      data: {
        group: {},
        players: []
      },
      maxValues: {}
    },
    advancedPerformanceComparisonStats: {
      loading: false,
      data: {
        group: {},
        players: []
      },
      maxValues: {}
    },
    charts: {
      data: [],
      loading: false
    },
    filtersDialog: false,
    selectedLayoutObject: null,
    selectedVariablesObject: null,
    dashboard: DashboardType.TEAM_ADVANCED_PERFORMANCE
  }),
  computed: {
    EventType() {
      return EventType
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasLayoutManagement() {
      return this.user && this.user.organisation_settings?.has_layout_management
    },
    currentTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    },
    playersByTeam() {
      const teamId = this.currentTeam.id || this.teams[0].id

      return this.allPlayers.data.filter(p => p.team_id === teamId)
    },
    teams() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    adjustedGridLayout() {
      if (!this.selectedLayout || !this.selectedLayout.grid) return []

      const tableRowCount = this.advancedPerformanceStats?.data?.players?.length || 0

      return adjustGridForTableHeights(this.selectedLayout.grid, tableRowCount, 0)
    },
    individualChartKeys() {
      return this.charts.data.filter(chart => chart.is_individual).map(chart => chart.key)
    },
    layoutVariables() {
      return this.selectedLayout ? [{ sortable: false, class: 'pr-0' }, { value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.tableHeaders.map(header => {
        return {
          ...header,
          text: this.$t(`variables.${header.value}`)
        }
      }), ['order'])) : []
    },
    layoutsDashboard() {
      return this.$store.getters[this.$storeGetters.APP_LAYOUTS]
        .filter(layout => layout.dashboard === DashboardType.TEAM_ADVANCED_PERFORMANCE)
    },
    layoutPlayer() {
      return this.$store.getters[this.$storeGetters.APP_LAYOUTS]
        .filter(layout => layout.dashboard === DashboardType.TEAM_ADVANCED_PERFORMANCE && layout.is_individual)[0]
    },
    layouts() {
        return this.$store.getters[this.$storeGetters.APP_LAYOUTS]
          .filter(layout => layout.dashboard === DashboardType.TEAM_ADVANCED_PERFORMANCE)
    },
    selectedLayout: {
      get() {
        if (this.selectedLayoutObject) {
          return this.selectedLayoutObject
        }
        return this.layoutsDashboard[0]
      },
      set(value) {
        this.selectedLayoutObject = value
      }
    },
    tableHeaders: {
      get() {
        if (this.selectedVariablesObject) {
          return this.selectedVariablesObject
        } else if (this.selectedLayout) {
          return this.selectedLayout.variables
        }
        return []
      },
      set(value) {
        this.selectedVariablesObject = value
      }
    },
    maxValues() {
      if (this.advancedPerformanceComparisonStats.data.players.length) {
        return this.advancedPerformanceComparisonStats.maxValues
      } else {
        return this.advancedPerformanceStats.maxValues
      }
    },
    filteredPlayers() {
      if (this.advancedPerformanceComparisonStats.data.players.length) {
        return this.advancedPerformanceComparisonStats.data.players || []
      } else {
        return this.advancedPerformanceStats.data.players || []
      }
    },
    teamStats() {
      if (this.advancedPerformanceComparisonStats.data.players.length) {
        return this.advancedPerformanceComparisonStats.data.group || {}
      } else {
        return this.advancedPerformanceStats.data.group || {}
      }
    }
  },
  watch: {
    currentTeam(value) {
      this.advancedFilters = {
        dateRange: [this.$dayjs().subtract(1, 'month').format('YYYY-MM-DD'), this.$dayjs().format('YYYY-MM-DD')],
        eventIds: [],
        teamId: 'all',
        teamIds: [value.id],
        playerIds: [],
        exerciseIds: [],
        eventType: null
      }
      this.fetchAdvancedPerformanceStats()
    },
    'advancedFilters.dateRange': function () {
      this.fetchEvents()
      this.fetchTrainingExercises()
    },
    'advancedFilters.eventType': function () {
      this.fetchEvents()
      this.fetchTrainingExercises()
    },
    'advancedFilters.eventIds': function () {
      this.fetchTrainingExercises()
    },
    'advancedFilters.playerIds': function () {
      this.fetchTrainingExercises()
    },
    'advancedCompareFilters.dateRange': function () {
      this.fetchComparisonEvents()
      this.fetchComparisonTrainingExercises()
    },
    'advancedCompareFilters.eventType': function () {
      this.fetchComparisonEvents()
      this.fetchComparisonTrainingExercises()
    },
    'advancedCompareFilters.eventIds': function () {
      this.fetchComparisonTrainingExercises()
    },
    'advancedCompareFilters.playerIds': function () {
      this.fetchComparisonTrainingExercises()
    },
    'advancedPerformanceStats.data.players': function (val) {
      if (val) {
        this.selectedLayout = this.layouts[0]
      }
    },
    selectedLayout() {
      this.selectedVariablesObject = null
    }
  },
  created() {
    this.eventTypes = EventTypes

    this.mainEventTypesList = Object.keys(EventTypes).map(k => {
      return {
        ...EventTypes[k],
        key: k
      }
    }).filter(e => e.is_other === false)

    let setPlayersAsFilter = false

    if (this.$route.query.from) {
      this.advancedFilters.dateRange = [this.$route.query.from, this.$route.query.to]
      this.advancedFilters.playerIds = [this.$route.query.player_id]
      this.advancedFilters.teamIds = [this.$route.query.team_id]

      this.$router.replace({ query: {} })
    } else {
      this.advancedFilters.dateRange = [this.$dayjs().subtract(1, 'month').format('YYYY-MM-DD'), this.$dayjs().format('YYYY-MM-DD')]
      this.advancedFilters.teamIds = [this.currentTeam.id]
      setPlayersAsFilter = true
      this.advancedCompareFilters.dateRange = [this.$dayjs().subtract(1, 'month').format('YYYY-MM-DD'), this.$dayjs().format('YYYY-MM-DD')]
    }

    EventBus.$on(this.$events.LAYOUT_UPDATED, (event) => {
      this.selectedLayoutObject = null
    })

    EventBus.$on(this.$events.LAYOUT_DELETED, (layout) => {
      this.selectedLayoutObject = this.layouts.filter(l => l.id !== layout.id)[0]
    })

    EventBus.$on(this.$events.LAYOUT_UPDATED, () => {
      this.selectedLayoutObject = null
    })

    this.fetchVariables()
    this.fetchCharts()
    this.fetchEvents()
    this.fetchAllPlayers(setPlayersAsFilter)
    this.fetchPlayerPositions()
    this.fetchPlayerStatuses()
    this.fetchTrainingExercises()
  },
  methods: {
    orderBy,
    fetchEvents: async function () {
      this.events.loading = true

      try {
        const response = await EventApiService.getAll({
          order_by: 'start_at',
          order: 'desc',
          has_integration: true,
          only_own_events: true,
          from: this.advancedFilters.dateRange[0],
          to: this.advancedFilters.dateRange[1],
          team_id: this.currentTeam.id,
          search: this.advancedFilters.eventName,
          type: this.advancedFilters.eventType,
          per_page: 100,
          page: 1
        })

        this.events.data = response.data
      } catch (e) {
        this.events.data = []
        throw e
      } finally {
        this.events.loading = false
      }
    },
    fetchComparisonEvents: async function () {
      this.comparisonEvents.loading = true

      try {
        const response = await EventApiService.getAll({
          order_by: 'start_at',
          order: 'desc',
          has_integration: true,
          only_own_events: true,
          from: this.advancedCompareFilters.dateRange[0],
          to: this.advancedCompareFilters.dateRange[1],
          team_id: this.currentTeam.id,
          type: this.advancedCompareFilters.eventType,
          per_page: 100,
          page: 1
        })

        this.comparisonEvents.data = response.data
      } catch (e) {
        this.comparisonEvents.data = []
        throw e
      } finally {
        this.comparisonEvents.loading = false
      }
    },
    fetchEventsDebounced: debounce(function () {
      this.fetchEvents()
    }, 500),
    getColorFromValue(value, max, reverse = false) {
      return getColorForValue(value, max, reverse)
    },
    async fetchPlayerPositions() {
      if (this.playerPositions.loaded) return

      try {
        this.playerPositions.loading = true

        const response = await PlayerPositionApiService.getAll({ sport_id: this.currentTeam.sport.id })

        this.playerPositions.data = response.data.map((position) => {
          return {
            id: position.id,
            name: this.$t(`player_position.${toCamelCase(position.name)}`),
            short: this.$t(`player_position.${toCamelCase(position.name)}.short`)
          }
        })
        this.playerPositions.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerPositions.loading = false
      }
    },
    async fetchPlayerStatuses() {
      if (this.playerStatuses.loaded) return

      try {
        this.playerStatuses.loading = true

        const response = await PlayerStatusApiService.getAll()

        this.playerStatuses.data = response.data.map((status) => {
          return {
            id: status.id,
            name: this.$t(`player_status.${status.name}`)
          }
        })
        this.playerStatuses.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerStatuses.loading = false
      }
    },
    async fetchAllPlayers(setAsFilter = false) {
      try {
        this.allPlayers.loading = true

        const result = await PlayerApiService.getSimpleList({
          order_by: 'last_name',
          order: 'ASC'
        })

        this.allPlayers.data = result.data

        if (setAsFilter) this.advancedFilters.playerIds = this.playersByTeam.map(p => p.id)

        await this.$nextTick()

        await this.fetchAdvancedPerformanceStats()
      } finally {
        this.allPlayers.loading = false
      }
    },
    async fetchAdvancedPerformanceStats() {
      if (this.advancedFilters.playerIds.length === 0 && this.playersByTeam.length === 0) return

      if (this.advancedPerformanceStats.loading) return

      this.advancedPerformanceComparisonStats.data = {
        group: {},
        players: []
      }

      try {
        this.advancedPerformanceStats.loading = true

        const params = {
          player_ids: this.advancedFilters.playerIds.length === 0 ? this.playersByTeam.map(p => p.id) : this.advancedFilters.playerIds,
          event_types: this.advancedFilters.eventType ? [this.advancedFilters.eventType] : undefined,
          event_ids: this.advancedFilters.eventIds,
          date_from: this.advancedFilters.dateRange[0] ?? undefined,
          date_to: this.advancedFilters.dateRange[1] ?? undefined,
          exercise_ids: this.advancedFilters.exerciseIds
        }

        const result = await EventApiService.getAdvancedPerformanceStats(params)

        this.advancedPerformanceStats.data = result

        this.advancedPerformanceStats.maxValues = processMaxValues(result.players.map(player => player.player_stats || {}), 1.1)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_advanced_statistics'),
          color: 'error'
        })

        throw e
      } finally {
        this.advancedPerformanceStats.loading = false
      }
    },
    async fetchAdvancedPerformanceComparisonStats() {
      if (this.advancedFilters.playerIds.length === 0 && this.playersByTeam.length === 0) return

      if (this.advancedPerformanceComparisonStats.loading) return

      try {
        this.advancedPerformanceComparisonStats.loading = true

        const params = {
          player_ids: this.advancedFilters.playerIds.length === 0 ? this.playersByTeam.map(p => p.id) : this.advancedFilters.playerIds,
          num_event_types: this.advancedFilters.eventType ? [this.advancedFilters.eventType] : undefined,
          num_event_ids: this.advancedFilters.eventIds,
          num_date_from: this.advancedFilters.dateRange[0] ?? undefined,
          num_date_to: this.advancedFilters.dateRange[1] ?? undefined,
          num_exercise_ids: this.advancedFilters.exerciseIds,
          num_official_match: this.advancedFilters.onlyOfficialMatch,
          denum_event_types: this.advancedCompareFilters.eventType ? [this.advancedCompareFilters.eventType] : undefined,
          denum_event_ids: this.advancedCompareFilters.eventIds,
          denum_date_from: this.advancedCompareFilters.dateRange[0] ?? undefined,
          denum_date_to: this.advancedCompareFilters.dateRange[1] ?? undefined,
          denum_exercise_ids: this.advancedCompareFilters.exerciseIds,
          denum_official_match: this.advancedCompareFilters.onlyOfficialMatch
        }

        const result = await EventApiService.getAdvancedPerformanceComparisonStats(params)

        this.advancedPerformanceComparisonStats.data = result

        this.advancedPerformanceComparisonStats.maxValues = Object.fromEntries(Object.keys(result.players[0]?.player_stats || {}).map(key => [key, 100]))
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_advanced_statistics'),
          color: 'error'
        })

        throw e
      } finally {
        this.advancedPerformanceComparisonStats.loading = false
      }
    },
    async fetchVariables() {
      this.variables.loading = true

      try {
        const result = await VariableApiService.getVariables({
          category: dashboardConfig[DashboardType.TEAM_ADVANCED_PERFORMANCE].variable_categories
        })

        this.variables.data = result.data.map(v => {
          return {
            ...v,
            text: v.suffix ? this.$t(`variables.${v.value}`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}`),
            long_text: v.suffix ? this.$t(`variables.${v.value}.long`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}.long`)
          }
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_variables.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.variables.loading = false
      }
    },
    saveLayoutVariables: debounce(async function () {
      if (!this.hasLayoutManagement) return

      this.submitting = true

      try {
        const data = {
          variables: this.tableHeaders.map(v => ({
            id: v.id,
            order: v.order
          }))
        }

        await LayoutApiService.saveVariables(this.selectedLayout.id, data)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.variables_save.error'),
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    }, 1000),
    async fetchCharts() {
      try {
        this.charts.loading = true

        const response = await ChartApiService.getCharts()

        this.charts.data = response.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.variables_fetch.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.charts.loading = false
      }
    },

    getVariableName(value) {
      return this.variables.data.find(variable => variable.value === value)?.text
    },
    getVariableSuffix(value) {
      return this.variables.data.find(variable => variable.value === value)?.suffix
    },
    openAddLayoutDialog() {
      EventBus.$emit(this.$events.ADD_LAYOUT, {
        dashboard: this.dashboard
      })
    },
    openEditLayoutDialog() {
      EventBus.$emit(this.$events.EDIT_LAYOUT, {
        dashboard: this.dashboard,
        selectedLayout: this.selectedLayout
      })
    },
    async fetchTrainingExercises() {
      this.exercises.loading = true

      try {
        const result = await EventApiService.getAllExercisesFromEvents({
          from: this.advancedFilters.dateRange[0],
          to: this.advancedFilters.dateRange[1],
          ids: this.advancedFilters.eventIds,
          type: this.advancedFilters.eventType,
          player_ids: this.advancedFilters.playerIds
        })

        this.exercises.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_events.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.exercises.loading = false
      }
    },
    async fetchComparisonTrainingExercises() {
      this.comparisonExercises.loading = true

      try {
        const result = await EventApiService.getAllExercisesFromEvents({
          from: this.advancedCompareFilters.dateRange[0],
          to: this.advancedCompareFilters.dateRange[1],
          ids: this.advancedCompareFilters.eventIds,
          type: this.advancedCompareFilters.eventType,
          player_ids: this.advancedCompareFilters.playerIds
        })

        this.comparisonExercises.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_events.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.comparisonExercises.loading = false
      }
    },
    selectPlayer(player) {
      this.$router.push({ name: RouteNames.PLAYER_EVENTS, params: { id: player.id }, query: { dateRange: this.advancedFilters.dateRange, eventIds: this.advancedFilters.eventIds, eventType: this.advancedFilters.eventType } })
    },
    setLayoutReady() {
      setTimeout(() => {
        this.layoutReady = true
      }, 100)
    }

  }
}
</script>

<style scoped>
</style>
